import {
  useRadarOverlaysSettings,
  type RadarOverlayItem,
} from '@ais-ui/user-settings';
import type { RadarOverlayInfo, Settings } from '@ais-ui/websockets';

/**
 * Read-only helper used by the Settings list page. The radar-overlays list is
 * held in the legacy blob's `radarSettings[]`, which stays the authoritative
 * source while we dual-write to the user-settings service.
 */
export const getRadarSourcesFromSettings = (
  settings: Settings,
): RadarOverlayInfo[] => {
  return settings?.radarSettings ?? [];
};

/**
 * CRUD hook for radar overlays. Every write is delegated to
 * `useRadarOverlaysSettings`, which rewrites the whole blob array (authoritative
 * through phase 2) AND best-effort mirrors the matching per-item op to the new
 * user-settings microservice, so callers get dual-write parity for free.
 */
export function useRadarSettings() {
  const { data, isLoading, source, add, update, remove } =
    useRadarOverlaysSettings();

  // These close over `data`, which is derived fresh each render, so memoizing
  // them would buy no referential stability — keep them as plain functions.
  const addRadar = (radar: RadarOverlayItem) => add(radar);

  const updateRadar = (radar: RadarOverlayItem) => update(radar);

  const removeRadarSource = (id: string) => remove(id);

  const setRadarEnabled = (id: string, enabled: boolean) => {
    const radar = data.find((r) => r.id === id);
    if (!radar || radar.enabled === enabled) return;
    return update({ ...radar, enabled });
  };

  const toggleRadarLayer = (id: string) => {
    const radar = data.find((r) => r.id === id);
    if (!radar) return;
    return update({ ...radar, enabled: !radar.enabled });
  };

  return {
    radarSources: data,
    isLoading,
    source,
    addRadar,
    updateRadar,
    removeRadarSource,
    setRadarEnabled,
    toggleRadarLayer,
  };
}
